import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import useSWR from 'swr'
import { BigNumber, Contract } from 'utils/ethersImport'
import commonStore from '@store/commonStore'
import priceStore from '@store/priceStore'
import { expandDecimals } from '@components/trade/rfx/tradingview/lib/numbers'
import {
  getConfFromPriceFeed,
  getPriceFeedTokenInfo,
  getPriceFromPriceFeed,
  tokenbaseIdMapping,
  tokenPythIdMapping,
  tokenStorkBaseIdMapping,
  tokenStorkIdMapping,
} from './utils'
import { getProvider } from 'rfx/lib/rpc'
import { getContract } from 'config/contracts'
import PythABI from 'abis/PythABI.json'
import { EvmPriceServiceConnection, PriceFeed } from '@pythnetwork/pyth-evm-js'
import { IS_VERBOSE } from 'config/development'
import { helperToast } from 'rfx/lib/helperToast'

const WS_URL = 'wss://api.wss.rfx.exchange'
const USD_DECIMALS = 30
const DEFAULT_TOKEN_DECIMALS = 18

const onPriceUpdate = (priceFeed: PriceFeed) => {
  const setTokenPrice = priceStore.getState().setTokenPrice
  try {
    const price = getPriceFromPriceFeed(priceFeed)
    const conf = getConfFromPriceFeed(priceFeed)
    const pythId = '0x' + priceFeed.id
    const spreadReductionBps = priceStore.getState().spreadReductionBps
    const spreadFactor = BigNumber.from(10000).sub(
      BigNumber.from(spreadReductionBps?.[pythId] || 0),
    )
    const spread = BigNumber.from(conf).mul(spreadFactor).div(10000)

    setTokenPrice(pythId, {
      maxPrice: price.add(spread),
      minPrice: price.sub(spread),
      conf: spread,
      ts: new Date().getTime(),
    })
  } catch (err) {
    // eslint-disable-next-line no-console
    IS_VERBOSE && console.log('🚀 ~ handleIncomingMessage ~ err:', err)
  }
}
const evmPriceServiceWsConnection = new EvmPriceServiceConnection(
  'https://hermes.pyth.network',
  {},
)

const fallbackPriceFetcher = async (
  chainId: number,
  setIsWSBlocked: (isWSBlocked: boolean) => void,
) => {
  try {
    // eslint-disable-next-line no-console
    IS_VERBOSE && console.log('Pyth WS: Starting WS')
    await evmPriceServiceWsConnection.subscribePriceFeedUpdates(
      Object.keys({
        ...tokenPythIdMapping(chainId),
        ...tokenbaseIdMapping(chainId),
      }),
      onPriceUpdate,
    )
    // eslint-disable-next-line no-console
    IS_VERBOSE && console.log('Pyth WS: Started WS Successfully')
    setIsWSBlocked(false)
  } catch (err) {
    helperToast.error('Failed to fetch prices from websocket')
    // eslint-disable-next-line no-console
    IS_VERBOSE && console.log('Pyth WS: Failed to start WS', err)
    setIsWSBlocked(true)
  }
}

const usePythPricesFetcher = (chainId: number) => {
  const socketRef = useRef<WebSocket | null>(null)
  const { pathname } = useRouter()

  const setTokenPrice = priceStore((s) => s.setTokenPrice)
  const [reconnectCount, setReconnectCount] = useState(0)
  const setPricesUsingApi = commonStore((s) => s.setPricesUsingApi)
  const isWSBlocked = commonStore((s) => s.isWSBlocked)
  const setIsWSBlocked = commonStore((s) => s.setIsWSBlocked)
  const prices = commonStore((s) => s.prices)
  const subscribeToPrices = () => {
    const assetIds = Object.keys({
      ...tokenStorkIdMapping(chainId),
      ...tokenStorkBaseIdMapping(chainId),
    })
    // eslint-disable-next-line no-console
    console.log({ assetIds })
    socketRef?.current?.send(
      JSON.stringify({ type: 'subscribe', asset_ids: assetIds }),
    )
    setIsWSBlocked(false)
  }

  const handleMessage = (event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data)
      if (!data?.prices) {
        return
      }

      evmPriceServiceWsConnection.closeWebSocket()

      const tokensMapping = {
        ...tokenStorkIdMapping(chainId),
        ...tokenStorkBaseIdMapping(chainId),
      }

      Object.values(data.prices).forEach((item: any) => {
        const price = BigNumber.from(item.price)
        const tokenDecimals = USD_DECIMALS - DEFAULT_TOKEN_DECIMALS
        const value = Object.keys(tokenStorkBaseIdMapping(chainId)).includes(
          item.asset_id,
        )
          ? tokensMapping[item.asset_id]?.['baseId']
          : tokensMapping[item.asset_id]?.['pythId']

        const adjustedPrice = price.mul(expandDecimals(1, tokenDecimals))
        setTokenPrice(value || '', {
          maxPrice: adjustedPrice,
          minPrice: adjustedPrice,
          conf: BigNumber.from(0),
          ts: Date.now(),
        })
      })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('WebSocket message parse error:', err)
    }
  }

  const reconnect = () => {
    if (reconnectCount > 3 && !socketRef.current) {
      return
    }
    setTimeout(() => {
      setReconnectCount((prev) => prev + 1)
      if (
        !socketRef.current ||
        socketRef.current.readyState === WebSocket.CLOSED
      ) {
        socketRef.current = new WebSocket(WS_URL)
        socketRef.current.onopen = subscribeToPrices
        socketRef.current.onmessage = handleMessage
        socketRef.current.onerror = (err) => {
          // eslint-disable-next-line no-console
          console.error('WebSocket error:', err)
        }
        socketRef.current.onclose = reconnect
      }
    }, 2000) // Wait 1s before reconnecting
  }

  useEffect(() => {
    if (isWSBlocked && reconnectCount <= 3) {
      fallbackPriceFetcher(chainId, setIsWSBlocked)
      return
    }
    const socket = new WebSocket(WS_URL)
    socketRef.current = socket

    socket.onopen = subscribeToPrices
    socket.onmessage = handleMessage
    socket.onerror = (err) => {
      setIsWSBlocked(true)
      socket.close()
      // eslint-disable-next-line no-console
      console.error('WebSocket error:', err)
    }
    socket.onclose = () => {
      reconnect()
      // eslint-disable-next-line no-console
      console.warn('WebSocket closed')
    }

    return () => {
      socket.close()
      socketRef.current = null
    }
  }, [chainId, isWSBlocked])

  useSWR(
    [
      'stork-price',
      { ...tokenStorkIdMapping(chainId), ...tokenStorkBaseIdMapping(chainId) },
      chainId,
    ],
    async ([, mapPythId]) => {
      try {
        const response = await fetch(
          `/stork/prices/latest?assets=${Object.keys(mapPythId)}`,
          {
            headers: {
              Authorization: `Basic ${process.env.NEXT_PUBLIC_STORK_API_KEY}`,
            },
          },
        )
        const latestFeed = await response.json()
        const tokenDecimals = USD_DECIMALS - DEFAULT_TOKEN_DECIMALS
        let priceInfo: Record<string, any> = {}

        Object.keys(mapPythId).forEach((item) => {
          const value = Object.keys(tokenStorkBaseIdMapping(chainId)).includes(
            item,
          )
            ? mapPythId[item]['baseId']
            : mapPythId[item]['pythId']
          const rawPrice = BigNumber.from(latestFeed.data[item || ''].price)
          const adjustedPrice = rawPrice.mul(expandDecimals(1, tokenDecimals))
          priceInfo[value || ''] = {
            maxPrice: adjustedPrice,
            minPrice: adjustedPrice,
            conf: BigNumber.from(0),
          }
        })

        setPricesUsingApi({ ...prices, ...priceInfo })

        return {
          priceInfo,
          updatedAt: latestFeed?.data['WETHUSD']?.timestamp,
        }
      } catch (e) {
        try {
          const mapIds = tokenPythIdMapping(chainId)
          const connection = new EvmPriceServiceConnection(
            'https://hermes.pyth.network',
            {},
          )
          const latestFeed = (await connection.getLatestPriceFeeds(
            Object.keys(mapIds),
          )) as PriceFeed[]
          let priceInfo = {}
          latestFeed.forEach((result) => {
            const tokenPriceInfo = getPriceFeedTokenInfo({
              _tokenPythIdMapping: tokenPythIdMapping(chainId),
              tokenId: result.id,
              priceFeed: result,
              chainId,
            })
            priceInfo = {
              ...tokenPriceInfo,
              ...priceInfo,
            }
          })
          setPricesUsingApi({ ...prices, ...priceInfo })
          return {
            priceInfo,
            updatedAt: latestFeed[0].getPriceUnchecked().publishTime,
          }
        } catch (e) {
          helperToast.error('Failed to fetch prices.')
          // eslint-disable-next-line no-console
          console.log('Error fetching prices from APIs:', e)
        }
      }
    },
    {
      refreshInterval: isWSBlocked || pathname === '/earn' ? 4000 : undefined,
      revalidateOnFocus: isWSBlocked,
      keepPreviousData: true,
    },
  )

  return {}
}

export default usePythPricesFetcher

export const querySpreadReductionBps = async (
  feedId: string,
  chainId: number,
): Promise<number> => {
  const provider = getProvider(undefined, chainId) as any

  const pythDataStreamProviderContract = new Contract(
    getContract(chainId, 'PythDataStreamProvider'),
    PythABI.abi,
    provider,
  )
  return await pythDataStreamProviderContract.spreadReductionBps(feedId)
}
